import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Grid, Button, Divider,
  TextField, Select, MenuItem, FormControl, Slider, CircularProgress,
  Paper, Card as MuiCard, CardContent, Chip
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import GridViewIcon from '@mui/icons-material/GridView';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AiActions } from '../../Services/_actions/index.js';

// ── Constants ────────────────────────────────────────────────────────────────
const PURPLE = 'primary.main';
const FONTS = [
  'Arial', 'Arial Black', 'Bahnschrift', 'Calibri', 'Cambria',
  'Candara', 'Comic Sans MS', 'Consolas', 'Constantia', 'Corbel',
  'Courier New', 'Franklin Gothic Medium', 'Gabriola', 'Gadugi',
  'Georgia', 'Impact', 'Ink Free', 'Javanese Text', 'Leelawadee UI',
  'Lucida Console', 'Lucida Sans Unicode', 'Malgun Gothic',
  'Microsoft Sans Serif', 'Mongolian Baiti', 'MS Gothic', 'MV Boli',
  'Palatino Linotype', 'Segoe Print', 'Segoe Script', 'Segoe UI',
  'Segoe UI Historic', 'Segoe UI Emoji', 'Segoe UI Symbol', 'SimSun',
  'Sitka Text', 'Sylfaen', 'Tahoma', 'Times New Roman', 'Trebuchet MS',
  'Verdana', 'Yu Gothic', 'Roboto', 'Open Sans', 'Lato', 'Montserrat',
  'Poppins', 'Inter', 'Nunito', 'Raleway', 'Ubuntu', 'Merriweather',
  'Playfair Display', 'Lora', 'Oswald', 'PT Sans', 'Rubik', 'Work Sans',
  'DM Sans', 'Manrope', 'Cabin', 'Fira Sans', 'Mulish', 'Karla',
  'Source Sans Pro', 'Noto Sans', 'Noto Serif', 'Bebas Neue', 'Anton',
  'Archivo', 'Barlow', 'Exo 2', 'Hind', 'Mukta', 'Quicksand',
  'Titillium Web', 'Varela Round', 'Josefin Sans', 'Libre Baskerville',
  'Cormorant Garamond', 'Crimson Text', 'Arvo', 'Bitter', 'Alegreya',
  'Dancing Script', 'Pacifico', 'Caveat', 'Satisfy',
  'Shadows Into Light', 'Indie Flower', 'Permanent Marker', 'Amatic SC',
  'Architects Daughter', 'Fira Code', 'JetBrains Mono', 'Roboto Mono',
  'Source Code Pro', 'Space Mono', 'IBM Plex Sans', 'IBM Plex Serif',
  'IBM Plex Mono'
];
const TEMPLATES = [
  { id: 'classic', label: 'Classic', desc: 'Title + bullets, clean and simple' },
  { id: 'sidebar', label: 'Sidebar', desc: 'Vertical color sidebar with brand name' },
  { id: 'split', label: 'Split 50/50', desc: 'Title block on left, content on right' },
  { id: 'header', label: 'Header Band', desc: 'Brand color band across the top' },
  { id: 'cardgrid', label: 'Card Grid', desc: 'Each bullet in its own card' },
  { id: 'editorial', label: 'Editorial', desc: 'Magazine-style layout' },
  { id: 'corporate', label: 'Corporate', desc: 'Professional business presentation' },
  { id: 'executive', label: 'Executive', desc: 'Boardroom style presentation' },
  { id: 'consulting', label: 'Consulting', desc: 'McKinsey/Bain style slides' },
  { id: 'financial', label: 'Financial', desc: 'Data-heavy finance reports' },
  { id: 'startup', label: 'Startup Pitch', desc: 'Investor pitch deck' },
  { id: 'saas', label: 'SaaS', desc: 'Software product presentation' },
  { id: 'productlaunch', label: 'Product Launch', desc: 'New product reveal' },
  { id: 'marketing', label: 'Marketing', desc: 'Campaign-focused presentation' },
  { id: 'socialmedia', label: 'Social Media', desc: 'Modern marketing deck' },
  { id: 'branding', label: 'Branding', desc: 'Brand strategy presentation' },
  { id: 'education', label: 'Education', desc: 'Classroom presentation' },
  { id: 'research', label: 'Research', desc: 'Academic research report' },
  { id: 'training', label: 'Training', desc: 'Employee training deck' },
  { id: 'creative', label: 'Creative', desc: 'Modern artistic design' },
  { id: 'minimal', label: 'Minimal', desc: 'Simple and elegant' },
  { id: 'modern', label: 'Modern', desc: 'Contemporary clean design' },
  { id: 'darkmode', label: 'Dark Mode', desc: 'Dark themed presentation' },
  { id: 'technology', label: 'Technology', desc: 'Tech industry slides' },
  { id: 'cybersecurity', label: 'Cyber Security', desc: 'Security-focused presentation' },
  { id: 'ai', label: 'Artificial Intelligence', desc: 'AI themed presentation' },
  { id: 'datascience', label: 'Data Science', desc: 'Analytics and insights' },
  { id: 'healthcare', label: 'Healthcare', desc: 'Medical and healthcare' },
  { id: 'automobile', label: 'Automobile', desc: 'Vehicle industry deck' },
  { id: 'realestate', label: 'Real Estate', desc: 'Property showcase' },
  { id: 'retail', label: 'Retail', desc: 'Retail business presentation' },
  { id: 'ecommerce', label: 'E-Commerce', desc: 'Online business deck' },
  { id: 'luxury', label: 'Luxury', desc: 'Premium high-end design' },
  { id: 'gold', label: 'Gold Executive', desc: 'Luxury gold accents' },
  { id: 'glassmorphism', label: 'Glassmorphism', desc: 'Modern glass effect UI' },
  { id: 'neumorphism', label: 'Neumorphism', desc: 'Soft UI design' }
];
const LOGO_OPTIONS = [
  { id: 'none', label: 'No logo', desc: "Don't include the company logo" },
  { id: 'header', label: 'Header', desc: 'Top corner of every slide' },
  { id: 'footer', label: 'Footer', desc: 'Bottom corner of every slide' },
  { id: 'titleonly', label: 'Title slide only', desc: 'Logo only on cover & closing' },
];
const SLIDE_STYLES = [
  { id: 'minimal', label: 'Minimal', desc: 'Lots of white space, thin lines' },
  { id: 'modern', label: 'Modern', desc: 'Clean contemporary design' },
  { id: 'corporate', label: 'Corporate', desc: 'Professional business presentation' },
  { id: 'executive', label: 'Executive', desc: 'Boardroom and leadership style' },
  { id: 'bold', label: 'Bold', desc: 'Large titles, strong color blocks' },
  { id: 'creative', label: 'Creative', desc: 'Unique layouts and visuals' },
  { id: 'playful', label: 'Playful', desc: 'Rounded shapes and vibrant accents' },
  { id: 'luxury', label: 'Luxury', desc: 'Premium high-end appearance' },
  { id: 'editorial', label: 'Editorial', desc: 'Magazine-inspired design' },
  { id: 'startup', label: 'Startup', desc: 'Modern investor pitch deck style' },
  { id: 'technology', label: 'Technology', desc: 'Futuristic tech-focused layout' },
  { id: 'data', label: 'Data Driven', desc: 'Charts, KPIs and analytics focused' },
  { id: 'dark', label: 'Dark Mode', desc: 'Dark backgrounds with highlights' },
  { id: 'glassmorphism', label: 'Glassmorphism', desc: 'Glass effect modern UI' },
  { id: 'neumorphism', label: 'Neumorphism', desc: 'Soft modern shadow effects' },
  { id: 'infographic', label: 'Infographic', desc: 'Visual storytelling layout' },
  { id: 'magazine', label: 'Magazine', desc: 'Editorial publication style' },
  { id: 'elegant', label: 'Elegant', desc: 'Sophisticated and refined design' },
  { id: 'academic', label: 'Academic', desc: 'Research and educational slides' },
  { id: 'marketing', label: 'Marketing', desc: 'Campaign and branding presentations' }
];
const TONES = [
  'Formal', 'Professional', 'Corporate', 'Executive', 'Conversational',
  'Friendly', 'Energetic', 'Inspirational', 'Persuasive', 'Academic',
  'Technical', 'Analytical', 'Data-Driven', 'Creative', 'Luxury',
  'Innovative', 'Motivational', 'Educational', 'Sales Focused', 'Investor Ready'
];
const DENSITIES = [
  'Very Low (1-2 points per slide)',
  'Low (3 points per slide)',
  'Medium (5 points per slide)',
  'High (7 points per slide)',
  'Very High (10+ points per slide)',
  'Presentation Style',
  'Executive Summary',
  'Balanced Content',
  'Detailed Analysis',
  'Research Report',
  'Training Material',
  'Data Heavy'
];

// ── Default brand state ───────────────────────────────────────────────────────
const DEFAULT_BRAND = {
  template: 'sidebar',
  logo_placement: 'header',
  color_primary: '#6D28D9',
  color_accent: '#F59E0B',
  color_background: '#FFFFFF',
  color_body: '#1F2937',
  heading_font: 'Calibri',
  body_font: 'Calibri',
  title_size: 42,
  body_size: 16,
  slide_style: 'playful',
  tone: 'Formal',
  density: 'Medium (5 per slide)',
};

type BrandState = typeof DEFAULT_BRAND;

// ── Helper to read org id from localStorage ──────────────────────────────────
const getOrganisationId = (): string => {
  const userInfo = localStorage?.getItem('userInfo');
  return userInfo
    ? JSON.parse(userInfo)?.profile?.organisation?.organisation_id ?? ""
    : "";
};

// ── Shared sub-components ────────────────────────────────────────────────────
const Card: React.FC<{ selected: boolean; onClick: () => void; children: React.ReactNode }> = ({ selected, onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      border: `2px solid ${selected ? PURPLE : '#e8e8f0'}`,
      borderRadius: '12px', cursor: 'pointer',
      bgcolor: selected ? 'rgba(124,92,252,0.04)' : '#fff',
      overflow: 'hidden',
      transition: 'all 0.2s',
      '&:hover': { borderColor: PURPLE, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
    }}
  >
    {children}
  </Box>
);

const StyledSelect: React.FC<{ label?: string; value: string; options: string[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <Box>
    {label && <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#555', mb: 0.8 }}>{label}</Typography>}
    <FormControl fullWidth>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          height: 48, borderRadius: '10px', fontSize: 13.5,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0ee' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PURPLE },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PURPLE, borderWidth: 1 },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '12px', mt: 0.5, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', maxHeight: 280,
              '& .MuiMenuItem-root': { fontSize: 13.5, py: 1.2, '&:hover': { bgcolor: 'rgba(124,92,252,0.06)' }, '&.Mui-selected': { bgcolor: 'rgba(124,92,252,0.1)', color: PURPLE, fontWeight: 600 } },
            },
          },
        }}
      >
        {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </Select>
    </FormControl>
  </Box>
);

const PtSlider: React.FC<{ label: string; value: number; min: number; max: number; onChange: (v: number) => void }> = ({ label, value, min, max, onChange }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}pt</Typography>
    </Box>
    <Slider
      value={value} onChange={(_, v) => onChange(v as number)} min={min} max={max}
      sx={{ color: PURPLE, '& .MuiSlider-thumb': { width: 18, height: 18, border: '2px solid #fff', boxShadow: `0 0 0 2px ${PURPLE}` }, '& .MuiSlider-rail': { bgcolor: '#e0e0ee' } }}
    />
  </Box>
);

// ── Live Preview Component ────────────────────────────────────────────────────
const LivePreview: React.FC<{ brand: BrandState }> = ({ brand }) => {
  // Get template display name
  const getTemplateName = () => {
    const template = TEMPLATES.find(t => t.id === brand.template);
    return template ? template.label : 'Sidebar';
  };

  const getStyleName = () => {
    const style = SLIDE_STYLES.find(s => s.id === brand.slide_style);
    return style ? style.label : 'Playful';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: '16px',
        border: '1px solid #e8e8f0',
        bgcolor: '#fafafe',
        position: 'sticky',
        top: 16,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <VisibilityIcon sx={{ fontSize: 18, color: '#666' }} />
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444' }}>
          Live Preview
        </Typography>
        <Chip
          label="Auto-updates"
          size="small"
          sx={{
            ml: 'auto',
            bgcolor: 'rgba(124,92,252,0.1)',
            color: PURPLE,
            fontSize: 10,
            fontWeight: 600,
            height: 20,
          }}
        />
      </Box>

      {/* Slide Preview */}
      <Box
        sx={{
          bgcolor: brand.color_background,
          borderRadius: '12px',
          p: 3,
          minHeight: 280,
          border: '1px solid #e0e0ee',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        }}
      >
        {/* Template-specific preview */}
        {brand.template === 'sidebar' && (
          <Box sx={{ display: 'flex', height: '100%', gap: 2 }}>
            <Box sx={{ width: 60, bgcolor: brand.color_primary, borderRadius: '8px', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: brand.heading_font,
                  fontSize: Math.min(brand.title_size * 0.5, 24),
                  fontWeight: 700,
                  color: brand.color_primary,
                  mb: 1,
                }}
              >
                Slide Title
              </Typography>
              <Typography
                sx={{
                  fontFamily: brand.body_font,
                  fontSize: Math.min(brand.body_size * 0.9, 14),
                  color: brand.color_body,
                  mb: 0.5,
                }}
              >
                • Key point one
              </Typography>
              <Typography
                sx={{
                  fontFamily: brand.body_font,
                  fontSize: Math.min(brand.body_size * 0.9, 14),
                  color: brand.color_body,
                  mb: 0.5,
                }}
              >
                • Important insight
              </Typography>
              <Typography
                sx={{
                  fontFamily: brand.body_font,
                  fontSize: Math.min(brand.body_size * 0.9, 14),
                  color: brand.color_body,
                }}
              >
                • Supporting detail
              </Typography>
              <Box sx={{ mt: 1.5, width: 40, height: 3, bgcolor: brand.color_accent, borderRadius: 2 }} />
            </Box>
          </Box>
        )}

        {brand.template === 'header' && (
          <Box>
            <Box sx={{ height: 24, bgcolor: brand.color_primary, borderRadius: '4px', mb: 2 }} />
            <Typography
              sx={{
                fontFamily: brand.heading_font,
                fontSize: Math.min(brand.title_size * 0.5, 24),
                fontWeight: 700,
                color: brand.color_body,
                mb: 1,
              }}
            >
              Slide Title
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
                mb: 0.5,
              }}
            >
              • Key point one
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
              }}
            >
              • Important insight
            </Typography>
            <Box sx={{ mt: 1, width: 40, height: 3, bgcolor: brand.color_accent, borderRadius: 2 }} />
          </Box>
        )}

        {brand.template === 'split' && (
          <Box sx={{ display: 'flex', height: '100%', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: brand.heading_font,
                  fontSize: Math.min(brand.title_size * 0.5, 24),
                  fontWeight: 700,
                  color: brand.color_primary,
                  mb: 1,
                }}
              >
                Slide Title
              </Typography>
              <Typography
                sx={{
                  fontFamily: brand.body_font,
                  fontSize: Math.min(brand.body_size * 0.9, 14),
                  color: brand.color_body,
                }}
              >
                Content on the right side
              </Typography>
            </Box>
            <Box sx={{ flex: 1, bgcolor: brand.color_primary + '15', borderRadius: '8px', p: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: brand.color_primary, fontWeight: 600 }}>
                Visual / Chart Area
              </Typography>
            </Box>
          </Box>
        )}

        {brand.template === 'cardgrid' && (
          <Box>
            <Typography
              sx={{
                fontFamily: brand.heading_font,
                fontSize: Math.min(brand.title_size * 0.5, 24),
                fontWeight: 700,
                color: brand.color_primary,
                mb: 1.5,
              }}
            >
              Slide Title
            </Typography>
            <Grid container spacing={1}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={4} key={i}>
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: brand.color_background,
                      border: `1px solid ${brand.color_primary}30`,
                      borderRadius: '6px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: brand.body_font,
                        fontSize: Math.min(brand.body_size * 0.7, 11),
                        color: brand.color_body,
                      }}
                    >
                      Card {i}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {brand.template === 'classic' && (
          <Box>
            <Typography
              sx={{
                fontFamily: brand.heading_font,
                fontSize: Math.min(brand.title_size * 0.5, 24),
                fontWeight: 700,
                color: brand.color_primary,
                mb: 1,
              }}
            >
              Slide Title
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
                mb: 0.5,
              }}
            >
              • Key point one
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
                mb: 0.5,
              }}
            >
              • Important insight
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
              }}
            >
              • Supporting detail
            </Typography>
          </Box>
        )}

        {brand.template === 'editorial' && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
              <Box sx={{ flex: 2 }}>
                <Typography
                  sx={{
                    fontFamily: brand.heading_font,
                    fontSize: Math.min(brand.title_size * 0.6, 28),
                    fontWeight: 700,
                    color: brand.color_primary,
                    lineHeight: 1.1,
                  }}
                >
                  Feature
                </Typography>
                <Typography
                  sx={{
                    fontFamily: brand.body_font,
                    fontSize: Math.min(brand.body_size * 0.9, 14),
                    color: brand.color_body,
                  }}
                >
                  • Key insight
                </Typography>
              </Box>
              <Box sx={{ flex: 1, bgcolor: brand.color_accent + '20', borderRadius: '4px', p: 1 }}>
                <Typography sx={{ fontSize: 10, color: brand.color_accent, fontWeight: 700 }}>
                  QUOTE
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {!['sidebar', 'header', 'split', 'cardgrid', 'classic', 'editorial'].includes(brand.template) && (
          <Box>
            <Typography
              sx={{
                fontFamily: brand.heading_font,
                fontSize: Math.min(brand.title_size * 0.5, 24),
                fontWeight: 700,
                color: brand.color_primary,
                mb: 1,
              }}
            >
              {getTemplateName()}
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
                mb: 0.5,
              }}
            >
              • Template preview
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
                mb: 0.5,
              }}
            >
              • Style: {getStyleName()}
            </Typography>
            <Typography
              sx={{
                fontFamily: brand.body_font,
                fontSize: Math.min(brand.body_size * 0.9, 14),
                color: brand.color_body,
              }}
            >
              • Tone: {brand.tone}
            </Typography>
            <Box sx={{ mt: 1.5, width: 40, height: 3, bgcolor: brand.color_accent, borderRadius: 2 }} />
          </Box>
        )}
      </Box>

      {/* Preview Info */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip
          label={`Template: ${getTemplateName()}`}
          size="small"
          sx={{ fontSize: 10, bgcolor: '#f0f0f5' }}
        />
        <Chip
          label={`Style: ${getStyleName()}`}
          size="small"
          sx={{ fontSize: 10, bgcolor: '#f0f0f5' }}
        />
        <Chip
          label={`Tone: ${brand.tone}`}
          size="small"
          sx={{ fontSize: 10, bgcolor: '#f0f0f5' }}
        />
        <Chip
          label={`Density: ${brand.density}`}
          size="small"
          sx={{ fontSize: 10, bgcolor: '#f0f0f5' }}
        />
      </Box>

      {/* Color Swatches */}
      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: brand.color_primary }} />
          <Typography sx={{ fontSize: 9, color: '#888' }}>Primary</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: brand.color_accent }} />
          <Typography sx={{ fontSize: 9, color: '#888' }}>Accent</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: brand.color_background, border: '1px solid #e0e0ee' }} />
          <Typography sx={{ fontSize: 9, color: '#888' }}>BG</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: brand.color_body }} />
          <Typography sx={{ fontSize: 9, color: '#888' }}>Text</Typography>
        </Box>
      </Box>

      {/* Font Preview */}
      <Box sx={{ mt: 1, display: 'flex', gap: 2, fontSize: 10, color: '#888' }}>
        <Typography sx={{ fontFamily: brand.heading_font, fontWeight: 700 }}>
          {brand.heading_font}
        </Typography>
        <Typography sx={{ fontFamily: brand.body_font }}>
          {brand.body_font}
        </Typography>
      </Box>
    </Paper>
  );
};

// ── Tab panels ──────────────────────────────────────────────────────────────

interface TabProps { brand: BrandState; setBrand: React.Dispatch<React.SetStateAction<BrandState>> }

const TemplateTab: React.FC<TabProps> = ({ brand, setBrand }) => (
  <Box sx={{ overflowY: 'scroll', height: '250px' }}>
    <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>Pick a slide layout. Colors and fonts stay the same.</Typography>
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {TEMPLATES.map((t) => (
        <Grid item xs={6} key={t.id}>
          <Card selected={brand.template === t.id} onClick={() => setBrand((p) => ({ ...p, template: t.id }))}>
            <Box sx={{ p: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t.label}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#9e9e9e' }}>{t.desc}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5 }}>Company logo placement</Typography>
    <Grid container spacing={1.5} sx={{ mb: 1 }}>
      {LOGO_OPTIONS.map((o) => (
        <Grid item xs={6} key={o.id}>
          <Card selected={brand.logo_placement === o.id} onClick={() => setBrand((p) => ({ ...p, logo_placement: o.id }))}>
            <Box sx={{ p: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{o.label}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#9e9e9e' }}>{o.desc}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>Uses the logo you uploaded for your company.</Typography>
  </Box>
);

const ColorsTab: React.FC<TabProps> = ({ brand, setBrand }) => {
  const refs = {
    color_primary: useRef<HTMLInputElement>(null),
    color_accent: useRef<HTMLInputElement>(null),
    color_background: useRef<HTMLInputElement>(null),
    color_body: useRef<HTMLInputElement>(null),
  };

  const fields: [keyof BrandState, string][] = [
    ['color_primary', 'Primary (titles, headers)'],
    ['color_accent', 'Accent (highlights, bars)'],
    ['color_background', 'Slide background'],
    ['color_body', 'Body text'],
  ];

  const set = (k: keyof BrandState) => (v: string) =>
    setBrand((p) => ({ ...p, [k]: v }));

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {fields.map(([k, label]) => (
          <Grid item xs={6} key={k}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#555', mb: 0.8 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0ee', borderRadius: '10px', overflow: 'hidden', height: 48 }}>
              <Box
                onClick={() => refs[k as keyof typeof refs]?.current?.click()}
                sx={{ width: 48, height: '100%', bgcolor: brand[k] as string, cursor: 'pointer', borderRight: '1px solid #e0e0ee', flexShrink: 0 }}
              />
              <input
                ref={refs[k as keyof typeof refs]}
                type="color"
                value={brand[k] as string}
                onChange={(e) => set(k)(e.target.value.toUpperCase())}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
              />
              <TextField
                value={brand[k] as string}
                onChange={(e) => set(k)(e.target.value)}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                inputProps={{ maxLength: 7, style: { fontSize: 13.5, paddingLeft: 12 } }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const TypographyTab: React.FC<TabProps> = ({ brand, setBrand }) => (
  <Box>
    <Grid container spacing={2} sx={{ mb: 2.5 }}>
      <Grid item xs={6}>
        <StyledSelect label="Heading font" value={brand.heading_font} options={FONTS} onChange={(v) => setBrand((p) => ({ ...p, heading_font: v }))} />
      </Grid>
      <Grid item xs={6}>
        <StyledSelect label="Body font" value={brand.body_font} options={FONTS} onChange={(v) => setBrand((p) => ({ ...p, body_font: v }))} />
      </Grid>
    </Grid>
    <PtSlider label="Title size" value={brand.title_size} min={20} max={72} onChange={(v) => setBrand((p) => ({ ...p, title_size: v }))} />
    <PtSlider label="Body size" value={brand.body_size} min={8} max={32} onChange={(v) => setBrand((p) => ({ ...p, body_size: v }))} />
  </Box>
);

const StyleToneTab: React.FC<TabProps> = ({ brand, setBrand }) => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>Slide style</Typography>
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {SLIDE_STYLES.map((s) => (
        <Grid item xs={6} key={s.id}>
          <Card selected={brand.slide_style === s.id} onClick={() => setBrand((p) => ({ ...p, slide_style: s.id }))}>
            <Box sx={{ p: 1.8 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>{s.desc}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <StyledSelect label="Tone of voice" value={brand.tone} options={TONES} onChange={(v) => setBrand((p) => ({ ...p, tone: v }))} />
      </Grid>
      <Grid item xs={6}>
        <StyledSelect label="Bullet density" value={brand.density} options={DENSITIES} onChange={(v) => setBrand((p) => ({ ...p, density: v }))} />
      </Grid>
    </Grid>
  </Box>
);

// ── Tab config ────────────────────────────────────────────────────────────────
const TAB_CONFIG = [
  { icon: <GridViewIcon sx={{ fontSize: 15 }} />, label: 'Template', Panel: TemplateTab },
  { icon: <PaletteIcon sx={{ fontSize: 15 }} />, label: 'Colors', Panel: ColorsTab },
  { icon: <TextFieldsIcon sx={{ fontSize: 15 }} />, label: 'Typography', Panel: TypographyTab },
  { icon: <TuneIcon sx={{ fontSize: 15 }} />, label: 'Style & Tone', Panel: StyleToneTab },
];

// ── Main component ────────────────────────────────────────────────────────────
const BrandPreferencesContent: React.FC<{
  onCancel: () => void;
  onSave: () => void;
  onReset: () => void;
  companyName?: string;
  initialBrand?: Partial<BrandState>;
}> = ({ onCancel, onSave, onReset, companyName, initialBrand }) => {
  const action = AiActions();
  const organisationId = getOrganisationId();

  const [tab, setTab] = useState(0);
  const [brand, setBrand] = useState<BrandState>({ ...DEFAULT_BRAND, ...initialBrand });

  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [recordExists, setRecordExists] = useState<boolean | null>(null);

  // ── Fetch existing brand prefs on mount ─────────────────────────────────
  useEffect(() => {
    if (!organisationId) { setIsFetching(false); return; }

    (async () => {
      setIsFetching(true);
      try {
        const res = await action.getOrganizationInstruction(organisationId);
        if (res && !res?.general_error) {
          setRecordExists(true);
          setBrand((prev) => {
            const next = { ...prev };
            (Object.keys(DEFAULT_BRAND) as (keyof BrandState)[]).forEach((k) => {
              if (res[k] !== undefined && res[k] !== null) {
                (next as any)[k] = res[k];
              }
            });
            return next;
          });
        } else {
          setRecordExists(false);
        }
      } catch {
        setRecordExists(false);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [organisationId]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!organisationId) { onSave(); return; }

    setIsSaving(true);
    try {
      const payload = {
        organization_id: organisationId,
        brand_preferences: {
          ...brand,
        },
        ...(companyName ? { company_name: companyName } : {}),
      };

      if (recordExists) {
        await action.updateOrganizationInstruction(payload);
      } else {
        await action.createOrganizationInstruction(payload);
        setRecordExists(true);
      }
    } catch (err) {
      console.error('Failed to save brand preferences:', err);
    } finally {
      setIsSaving(false);
      onSave();
    }
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setBrand({ ...DEFAULT_BRAND });
    onReset();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const { Panel } = TAB_CONFIG[tab];

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 3, height: '100%' }}>
        {/* Left Panel - Settings */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', height: '100%', pb: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
            <PaletteIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Brand Preferences</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#9e9e9e', mb: 2 }}>
            One-time setup. Every PPT generated for{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
              {companyName || 'your company'}
            </Box>{' '}
            will use these colors, fonts and style.
          </Typography>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 2.5,
              bgcolor: '#f6f6f9',
              borderRadius: '10px',
              px: 0.5,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 500,
                minHeight: 36,
                py: 0.8,
                px: 1.5,
                color: '#666',
                borderRadius: '8px',
              },
              '& .Mui-selected': {
                color: `${PURPLE} !important`,
                fontWeight: 700,
              },
              '& .MuiTabs-indicator': {
                bgcolor: PURPLE,
                height: 2,
                borderRadius: 2,
              },
            }}
          >
            {TAB_CONFIG.map((t) => (
              <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} />
            ))}
          </Tabs>

          {/* Tab content */}
          {isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Panel brand={brand} setBrand={setBrand} />
          )}

          <Divider sx={{ mt: 2, mb: 2 }} />

          {/* Footer actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, alignItems: 'center' }}>
            <Button
              onClick={handleReset}
              sx={{ textTransform: 'none', color: '#666', fontSize: 13 }}
            >
              ↺ Reset
            </Button>
            <Button
              onClick={onCancel}
              sx={{ textTransform: 'none', color: '#444', fontSize: 13, border: '1px solid #e0e0ee', borderRadius: '8px', px: 2 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isFetching || isSaving}
              startIcon={isSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
              sx={{
                textTransform: 'none',
                bgcolor: PURPLE,
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { bgcolor: '#6a4de0' },
                '&.Mui-disabled': { bgcolor: '#bbb', color: '#fff' },
              }}
            >
              {isSaving ? 'Saving…' : recordExists ? 'Update preferences' : 'Save preferences'}
            </Button>
          </Box>
        </Box>

        {/* Right Panel - Live Preview */}
        <Box sx={{ width: 380, flexShrink: 0, height: '100%', overflowY: 'auto', pb: 2 }}>
          <LivePreview brand={brand} />
        </Box>
      </Box>
    </Box>
  );
};

export default BrandPreferencesContent;